import { cloneDeep } from 'es-toolkit/object';
import moment from 'moment';
import { useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setQuoteBidExpirationDate, setQuoteModalBlocker } from '../../../../redux/common/CommonActions';
import { QUOTE_STATUSES } from '../../Shipment/components/QuoteCard/constants';
import { getSelectDateWithCurrentTime } from './utils';

export const initialBidDurationTimeError = {
  showModal: false,
  title: '',
};

export const getBidExpirationDateCorrectionValue = (selectTime) => {
  const userTimeZone = moment.tz.guess();
  const currentUserDate = moment.tz(userTimeZone).startOf('day');
  const currentUTCDate = moment.tz('UTC').startOf('day');

  if (!selectTime && currentUserDate.isAfter(currentUTCDate, 'days')) {
    return -1;
  }
  if (!selectTime && currentUserDate.isBefore(currentUTCDate, 'days')) {
    return 1;
  }
  return 0;
};

const useBidDuration = ({ form, quoteStatus, setShowBidDuration }) => {
  const dispatch = useDispatch();
  const [showWarning, setShowWarning] = useState(false);
  const [bidDurationTimeError, setBidDurationTimeError] = useState({
    showModal: false,
    title: '',
  });
  const [calculatedBidExpirationDate, setCalculatedBidExpirationDate] = useState(null);
  const quoteModalBlocker = useSelector((state) => state.common.quoteModalBlocker);
  const formDataRef = useRef(null);

  const validateTime = (bidExpirationDate, ianaTimeZoneId) => {
    if (!ianaTimeZoneId) {
      return {
        isTimeValid: true,
        currentTimeInTZ: null,
      };
    }

    const nowInSelectedTZ = moment().tz(ianaTimeZoneId);
    const deadlineInTargetTZ = moment(bidExpirationDate).tz(ianaTimeZoneId, true);
    const isTimeValid = deadlineInTargetTZ.isAfter(nowInSelectedTZ);

    return {
      isTimeValid,
      currentTimeInTZ: nowInSelectedTZ.format('DD/MM/YYYY HH:mm:ss A'),
    };
  };
  const handleOkBidDurationModal = (formValue, fromWarningPopup = false) => {
    if (quoteStatus === QUOTE_STATUSES.readyForAward && !fromWarningPopup) {
      setShowWarning(true);
      formDataRef.current = formValue;

      return;
    }

    const bidExpirationOffsetHours = Number(formValue.selectHours) || null;
    const bidExpirationTimeZoneInfo = formValue.SpotBidTimeZoneValue || null;
    const selectTime = formValue.selectTime;

    const selectDateWithCurrentTime =
      bidExpirationOffsetHours && cloneDeep(getSelectDateWithCurrentTime(formValue.selectDate));
    const selectDateWithAddedTime = bidExpirationOffsetHours
      ? selectDateWithCurrentTime.add(bidExpirationOffsetHours, 'm')
      : formValue.selectDate;

    const bidExpirationDate = formValue.selectDate?.set({
      hour: selectTime ? formValue.selectTime.hours() : 12,
      minute: selectTime ? formValue.selectTime.minutes() : 0,
      second: 0,
    });

    if (bidExpirationTimeZoneInfo) {
      const timeValidateInfo = validateTime(bidExpirationDate, formValue.ianaTimeZoneId);
      if (!timeValidateInfo.isTimeValid) {
        setBidDurationTimeError({
          showModal: true,
          title: `Please select time that is later than current time in the selected time zone. Current time: ${timeValidateInfo.currentTimeInTZ}`,
        });
        return;
      }
    }

    // variable to correct value that we send to BE while form submit
    const bidExpirationDateCorrectionValue = getBidExpirationDateCorrectionValue(selectTime);

    form.setFieldValue('bidExpirationDateCorrectionValue', bidExpirationDateCorrectionValue);

    if (!bidExpirationOffsetHours) {
      const timeValidateInfo = validateTime(bidExpirationDate, formValue.ianaTimeZoneId);

      if (!timeValidateInfo.isTimeValid) {
        setBidDurationTimeError({
          showModal: true,
          title: `Please select time that will bigger than current time in selected time zone. Now is ${timeValidateInfo.currentTimeInTZ}`,
        });

        return;
      }
    }

    form.setFieldValue('bidExpirationOffsetHours', bidExpirationOffsetHours);
    form.setFieldValue('bidExpirationTimeZoneInfo', bidExpirationTimeZoneInfo);
    form.setFieldValue('bidExpirationDate', bidExpirationDate);
    setCalculatedBidExpirationDate(bidExpirationOffsetHours ? selectDateWithAddedTime : bidExpirationDate);
    setShowBidDuration(false);
    dispatch(setQuoteBidExpirationDate(bidExpirationDate));
    !quoteModalBlocker && dispatch(setQuoteModalBlocker(true));

    const reset = !bidExpirationOffsetHours && !bidExpirationTimeZoneInfo && !bidExpirationDate;

    form.setFieldValue('isResetBidExpiration', reset);
  };

  const handleBidDurationWarningOK = () => {
    setShowWarning(false);

    handleOkBidDurationModal(formDataRef.current || {}, true);
  };

  const handleBidDurationWarningCancel = () => {
    setShowWarning(false);
    setShowBidDuration(false);
  };

  return {
    handleOkBidDurationModal,
    handleBidDurationWarningOK,
    handleBidDurationWarningCancel,
    showWarning,
    setShowWarning,
    bidDurationTimeError,
    setBidDurationTimeError,
    calculatedBidExpirationDate,
  };
};

export default useBidDuration;
